import { Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material';

// ── Cred 1 visual: creator/tool/metric collage ────────────────────────────────

const collageTiles: Array<
  | { kind: 'creator'; initials: string; handle: string; views: string; gradient: string }
  | { kind: 'tool'; mark: string; name: string; markBg: string }
  | { kind: 'metric'; v: string; k: string }
  | { kind: 'video'; handle: string }
  | { kind: 'wide' }
> = [
  // Row 1
  { kind: 'creator', initials: 'MJ', handle: '@maya.j', views: '2.1M', gradient: 'linear-gradient(135deg,#1BB3CB,#580F6B)' },
  { kind: 'creator', initials: 'DK', handle: '@devon.k', views: '840K', gradient: 'linear-gradient(135deg,#580F6B,#1BB3CB)' },
  { kind: 'video', handle: 'Hook v3' },
  { kind: 'creator', initials: 'SR', handle: '@sam.r', views: '1.4M', gradient: 'linear-gradient(135deg,#1BB3CB,#0a4f8a)' },
  { kind: 'tool', mark: 'A', name: 'AppsFlyer', markBg: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)' },
  { kind: 'tool', mark: 'Aj', name: 'Adjust', markBg: 'linear-gradient(135deg,#27c499,#0a8a5a)' },
  // Row 2 (video continues from row 1)
  { kind: 'creator', initials: 'LP', handle: '@lia.p', views: '3.2M', gradient: 'linear-gradient(135deg,#c44dff,#580F6B)' },
  { kind: 'metric', v: '−$0.92', k: 'CPI Δ' },
  { kind: 'tool', mark: 'Br', name: 'Branch', markBg: 'linear-gradient(135deg,#f9a826,#c4571b)' },
  { kind: 'creator', initials: 'TN', handle: '@tomas.n', views: '670K', gradient: 'linear-gradient(135deg,#0CAECF,#580F6B)' },
  { kind: 'creator', initials: 'RV', handle: '@rita.v', views: '1.9M', gradient: 'linear-gradient(135deg,#580F6B,#c44dff)' },
  // Row 3
  { kind: 'wide' },
  { kind: 'metric', v: '18', k: 'Variants' },
  { kind: 'creator', initials: 'JK', handle: '@jules.k', views: '512K', gradient: 'linear-gradient(135deg,#1BB3CB,#580F6B)' },
  { kind: 'metric', v: '2.4M', k: 'Avg views' },
];

const CollageTile = ({ tile }: { tile: typeof collageTiles[number] }) => {
  const base = {
    borderRadius: '10px',
    background: 'rgba(255,255,255,.03)',
    border: '1px solid rgba(255,255,255,.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
  } as const;

  if (tile.kind === 'creator') {
    return (
      <Box sx={{ ...base, background: 'linear-gradient(140deg, rgba(12,174,207,.18), rgba(88,15,107,.22))', borderColor: 'rgba(12,174,207,.3)' }}>
        <Box sx={{ width: 42, height: 42, borderRadius: '50%', background: tile.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>{tile.initials}</Box>
        <Typography sx={{ position: 'absolute', left: '8px', bottom: '6px', fontSize: '10px', color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>{tile.handle}</Typography>
        <Typography sx={{ position: 'absolute', right: '8px', top: '6px', fontSize: '9px', color: '#0CAECF', fontWeight: 600, background: 'rgba(0,0,0,.5)', px: '6px', py: '2px', borderRadius: '4px' }}>{tile.views}</Typography>
      </Box>
    );
  }
  if (tile.kind === 'tool') {
    return (
      <Box sx={{ ...base, background: 'rgba(255,255,255,.05)', borderColor: 'rgba(255,255,255,.1)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <Box sx={{ width: 24, height: 24, borderRadius: '6px', background: tile.markBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '11px' }}>{tile.mark}</Box>
          <Typography sx={{ fontSize: '9px', fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase', color: '#A0A0A0' }}>{tile.name}</Typography>
        </Box>
      </Box>
    );
  }
  if (tile.kind === 'metric') {
    return (
      <Box sx={{ ...base, background: 'linear-gradient(135deg, rgba(12,174,207,.12), rgba(88,15,107,.12))', borderColor: 'rgba(12,174,207,.25)', flexDirection: 'column', gap: '2px', p: '8px' }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{tile.v}</Typography>
        <Typography sx={{ fontSize: '8px', color: '#606060', textTransform: 'uppercase', letterSpacing: '.5px' }}>{tile.k}</Typography>
      </Box>
    );
  }
  if (tile.kind === 'video') {
    return (
      <Box sx={{ ...base, background: 'linear-gradient(160deg, rgba(255,255,255,.04), rgba(12,174,207,.08))', gridRow: 'span 2' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontSize: '12px' }}>▶</Box>
        <Typography sx={{ position: 'absolute', left: '8px', bottom: '6px', fontSize: '10px', color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>{tile.handle}</Typography>
      </Box>
    );
  }
  // wide — variant leaderboard
  return (
    <Box sx={{ ...base, gridColumn: 'span 2', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', px: '12px', gap: '2px' }}>
      <Typography sx={{ fontSize: '9px', color: '#606060', letterSpacing: '.5px', textTransform: 'uppercase' }}>Variant leaderboard</Typography>
      <Box sx={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: '22px' }}>
        {[60, 90, 45, 75, 30, 55].map((h, i) => (
          <Box key={i} sx={{ width: '6px', height: `${h}%`, background: i === 1 ? '#0CAECF' : `rgba(12,174,207,${0.3 + (h / 100) * 0.3})` }} />
        ))}
      </Box>
    </Box>
  );
};

const CollageVisual = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      p: { xs: '12px', sm: '24px' },
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)' },
      gridTemplateRows: { xs: 'repeat(5, 1fr)', sm: 'repeat(4, 1fr)' },
      gap: { xs: '6px', sm: '10px' },
    }}
  >
    {collageTiles.map((tile, i) => (
      <CollageTile key={i} tile={tile} />
    ))}
  </Box>
);

// ── Cred 2 visual: dashboard mock ─────────────────────────────────────────────

const dashKpis = [
  { k: 'Installs', v: '12,847', d: '▲ 18%', up: true },
  { k: 'Blended CPI', v: '$3.42', d: '▼ $0.91', up: true },
  { k: 'Top variant ROAS', v: '2.7x', d: '▲ 0.4', up: true },
  { k: 'Creators live', v: '14', d: '/ 15', up: false },
];

const dashRows = [
  { handle: '@maya.j', installs: '2,148', cpi: '$2.81', roas: '3.4x', dim: false, gradient: 'linear-gradient(135deg,#1BB3CB,#580F6B)', hi: true },
  { handle: '@rita.v', installs: '1,902', cpi: '$3.04', roas: '3.1x', dim: false, gradient: 'linear-gradient(135deg,#580F6B,#c44dff)', hi: true },
  { handle: '@devon.k', installs: '1,640', cpi: '$3.27', roas: '2.6x', dim: false, gradient: 'linear-gradient(135deg,#0CAECF,#0a4f8a)', hi: false },
  { handle: '@lia.p', installs: '1,415', cpi: '$3.58', roas: '2.4x', dim: false, gradient: 'linear-gradient(135deg,#c44dff,#580F6B)', hi: false },
  { handle: '@tomas.n', installs: '312', cpi: '$6.10', roas: '0.9x', dim: true, gradient: 'linear-gradient(135deg,#444,#222)', hi: false },
];

const DashboardVisual = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      p: { xs: '12px', sm: '20px' },
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: '8px', sm: '12px' },
      fontFamily: 'Inter, system-ui, sans-serif',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
      <Typography
        sx={{
          fontSize: { xs: '10px', sm: '12px' },
          fontWeight: 600,
          color: '#fff',
          letterSpacing: '.3px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        Creator Performance · Last 7 days
      </Typography>
      <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#0CAECF', background: 'rgba(12,174,207,.15)', border: '1px solid rgba(12,174,207,.4)', px: '8px', py: '2px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '.5px', display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
        <Box sx={{ width: '5px', height: '5px', borderRadius: '50%', background: '#0CAECF' }} />
        Live
      </Typography>
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: '6px', sm: '8px' } }}>
      {dashKpis.map(({ k, v, d, up }) => (
        <Box key={k} sx={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '8px', p: { xs: '6px 8px', sm: '8px 10px' }, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography sx={{ fontSize: '9px', color: '#606060', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 500 }}>{k}</Typography>
          <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{v}</Typography>
          <Typography sx={{ fontSize: '9px', color: up ? '#27c499' : '#606060' }}>{d}</Typography>
        </Box>
      ))}
    </Box>

    <Box sx={{ height: '48px', background: 'rgba(255,255,255,.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,.06)', p: '4px' }}>
      <Box component="svg" viewBox="0 0 320 64" preserveAspectRatio="none" sx={{ width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id="gline" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0CAECF" stopOpacity=".5" />
            <stop offset="100%" stopColor="#0CAECF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,48 L32,44 L64,40 L96,46 L128,32 L160,28 L192,30 L224,22 L256,18 L288,14 L320,10 L320,64 L0,64 Z" fill="url(#gline)" />
        <path d="M0,48 L32,44 L64,40 L96,46 L128,32 L160,28 L192,30 L224,22 L256,18 L288,14 L320,10" stroke="#0CAECF" strokeWidth="1.6" fill="none" />
      </Box>
    </Box>

    <Box sx={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '8px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', p: '6px 10px', fontSize: '9px', color: '#606060', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600, background: 'rgba(255,255,255,.04)' }}>
        <span>Creator</span><span>Installs</span><span>CPI</span><span>ROAS</span>
      </Box>
      {dashRows.map(({ handle, installs, cpi, roas, dim, gradient, hi }) => (
        <Box key={handle} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', p: '6px 10px', fontSize: '10px', color: dim ? '#606060' : '#e6e6e6', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box component="i" sx={{ width: '10px', height: '10px', borderRadius: '50%', background: gradient, display: 'inline-block' }} />
            {handle}
          </Box>
          <span>{installs}</span>
          <span>{cpi}</span>
          <Box component="span" sx={{ color: hi ? '#0CAECF' : 'inherit', fontWeight: hi ? 600 : 400 }}>{roas}</Box>
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Section ────────────────────────────────────────────────────────────────────

const credentials = [
  {
    label: 'Operators, not consultants',
    heading: "The entire creator stack — run for you, end-to-end.",
    description:
      "We don't drop in a deck and walk away — we operate the program. Creator sourcing, briefing, production, warmed-account distribution, attribution wiring, and weekly performance reviews run on our side. Your team approves; we ship.",
    visual: 'collage' as const,
  },
  {
    label: 'Attribution built in',
    heading: "We won't show you view counts and call it a win.",
    description:
      "Every creator and every TikTok account gets its own deep link, tied to your MMP — AppsFlyer, Adjust, Branch. You see installs and CPI broken down by creator. We double down on winners, cut underperformers. Reporting is tuned to the metric that matters to you — installs, CPI, or blended ROAS.",
    visual: 'dashboard' as const,
  },
];

const LPSectionProducts = () => {
  const theme = useTheme();
  const isMdOrBelow = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: { xs: 2, sm: 4 },
        pt: 10,
        pb: 10,
        [theme.breakpoints.down('md')]: { paddingTop: '0' }
      }}
    >
      {/* Section label + heading */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 1180,
          marginTop: '3rem',
          mb: 2,
          justifyContent: 'center',
          [theme.breakpoints.down('md')]: { marginTop: '2rem', maxWidth: '300px' }
        }}
      >
        <Typography
          fontFamily="Inter"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: '#0CAECF',
            mb: 2
          }}
        >
          Why Saagas
        </Typography>
        <Typography
          fontFamily="Inter"
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '24px', md: '34px' },
            color: '#FFFFFF',
            mb: 1,
            textAlign: 'center',
          }}
        >
          The creator engine, done for you.
        </Typography>
        <Typography
          fontFamily="Inter"
          sx={{
            fontWeight: 300,
            fontSize: { xs: '15px', md: '18px' },
            color: '#C0C0C0',
            textAlign: 'center',
            mt: 1,
            maxWidth: 720,
            mx: 'auto',
          }}
        >
          Most app teams stall at the warmed-account step. Most agencies don&apos;t have the tooling depth. We&apos;ve shipped this stack at Amazon scale — now we run it for you.
        </Typography>
      </Box>

      {/* Two alternating sections */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', [theme.breakpoints.down('md')]: { gap: '1rem' } }}>
        {credentials.map((cred, index) => {
          const isEven = index % 2 === 0;
          return (
            <Box
              key={cred.label}
              sx={{
                display: 'flex',
                flexDirection: isEven ? { xs: 'column-reverse', md: 'row' } : { xs: 'column', md: 'row-reverse' },
                width: '100%',
                maxWidth: 1260,
                gap: '40px',
                alignItems: 'center',
                mt: 6,
                mb: 4,
                [theme.breakpoints.down('md')]: { mt: 0, mb: 0, gap: '16px' }
              }}
            >
              {/* Text side */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: { xs: '100%', md: '360px' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography
                  fontFamily="Inter"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: '#0CAECF',
                    mb: 1.5
                  }}
                >
                  {cred.label}
                </Typography>
                <Typography
                  component="h3"
                  fontFamily="Inter"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '18px', md: '22px', lg: '26px' },
                    lineHeight: '120%',
                    color: '#FFFFFF',
                    mb: 2,
                  }}
                >
                  {cred.heading}
                </Typography>
                <Typography
                  fontFamily="Inter"
                  sx={{
                    fontWeight: 300,
                    lineHeight: '150%',
                    fontSize: { xs: '15px', md: '16px' },
                    color: '#c0c0c0',
                  }}
                >
                  {cred.description}
                </Typography>
              </Box>

              {/* Image side — rich visual frame */}
              <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 860,
                    border: '2px solid #fff',
                    borderRadius: '12px',
                    aspectRatio: { xs: '4 / 3', sm: '16 / 10' },
                    overflow: 'hidden',
                    background: 'radial-gradient(ellipse at top left, rgba(12,174,207,.08), transparent 60%), radial-gradient(ellipse at bottom right, rgba(88,15,107,.10), transparent 60%), #050505',
                  }}
                >
                  {cred.visual === 'collage' ? <CollageVisual /> : <DashboardVisual />}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Founder note placeholder + CTA */}
      <Box sx={{ maxWidth: 1300, mb: 8, px: { xs: 2 } }}>
        <Typography
          fontFamily="Inter"
          variant="h6"
          sx={{
            fontWeight: 300,
            fontSize: 18,
            color: '#C0c0c0',
            mt: 6,
            textAlign: 'center',
            fontStyle: 'italic',
            [theme.breakpoints.down('md')]: {
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '130%',
              letterSpacing: '1px',
              mt: 8
            }
          }}
        >
          &ldquo;More variants. Less founder load. Same conversion goal.&rdquo; — Yolando, Founder
        </Typography>
        <Box sx={{ mt: 4 }}>
          <Button
            component="a"
            href="https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              px: isMdOrBelow ? '15px' : '32px',
              py: isMdOrBelow ? '8px' : '16px',
              maxWidth: isMdOrBelow ? '200px' : '260px',
              maxHeight: isMdOrBelow ? '49px' : '52px',
              borderRadius: '170px',
              boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
              fontWeight: 700,
              fontSize: isMdOrBelow ? '14px' : '16px',
              background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(163deg, rgb(12, 160, 200) -29.1%, rgb(80, 10, 100) 96.45%)'
              }
            }}
          >
            Book a Discovery Call →
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LPSectionProducts;
